import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import {
  ActivityTypes,
  activityPricesStorage,
  getAppActivity,
} from "e.sic-platform.webclient/src/services/shop/ActivityPricesStorage";
import {
  OrderBreakdownRow,
  OrderBreakdownStorage,
} from "e.sic-platform.webclient/src/services/shop/OrderBreakdownStorage";
import { parseServerDate } from "e.sic-platform.webclient/src/helpers/DateHelpers";
import { Coin } from "../../../components/shop/Coin";

// The three services a file can carry on the Inspection Insights page.
export type ServiceKey = "ocr" | "analysis" | "segmentation";

type ServiceState = "running" | "success" | "failed" | "none";

interface Props {
  // Each service store (backend-derived status + startedAt/finishedAt on the base model).
  ocrService: any;
  analysisService: any;
  segmentationService: any;
  // Exact as-charged rows for the current file.
  orderStore: OrderBreakdownStorage;
  // Re-run a single (failed) service. Wired by the page.
  onRerun: (key: ServiceKey) => void;
  // Narrow layout for the results sidebar: a stacked list instead of a 4-column table.
  compact?: boolean;
  // Rendered inside a tab that already names it: no frame and no "Order & process details"
  // heading of its own (the file name is still shown).
  embedded?: boolean;
}

/**
 * Which per-service activity types each bundle price covers.
 *
 * A combined run is charged ONCE, as a single activity_logs row under the bundle type — so
 * looking a service up by its own type finds nothing. That is why every Charged cell showed
 * 0 credits on a combined run even though the file had plainly been billed (and the Total,
 * which sums the raw rows, was right all along).
 *
 * Segmentation on a combined run additionally gets a row of its OWN, deliberately billed at
 * 0 (it is included in the combination price) — see isFreeSegmentationMarker, which keeps
 * that row from being mistaken for the cycle's priced line.
 */
const BUNDLE_MEMBERS: Partial<Record<ActivityTypes, ActivityTypes[]>> = {
  [ActivityTypes.VideoAnalysisAndSegmentation]: [
    ActivityTypes.VideoAnalysis,
    ActivityTypes.HighlightDetection,
  ],
  [ActivityTypes.VideoOcrAnalysisAndSegmentation]: [
    ActivityTypes.VideoOcr,
    ActivityTypes.VideoAnalysis,
    ActivityTypes.HighlightDetection,
  ],
  [ActivityTypes.VideoOcrAndAnalysis]: [ActivityTypes.VideoOcr, ActivityTypes.VideoAnalysis],
  [ActivityTypes.VideoOcrAndSegmentation]: [
    ActivityTypes.VideoOcr,
    ActivityTypes.HighlightDetection,
  ],
};

// Every type that takes part in the bundling above — the ones a cycle's priced row can
// carry. Conversion and restore keep their own independent rows and never combine.
const BUNDLABLE_TYPES: ActivityTypes[] = [
  ActivityTypes.VideoOcr,
  ActivityTypes.VideoAnalysis,
  ActivityTypes.HighlightDetection,
  ActivityTypes.VideoOcrAndAnalysis,
  ActivityTypes.VideoAnalysisAndSegmentation,
  ActivityTypes.VideoOcrAndSegmentation,
  ActivityTypes.VideoOcrAnalysisAndSegmentation,
];

/**
 * The single type a set of services is billed as. Delegates to getAppActivity, which mirrors the
 * server's ActivityPricing.BundleTypeFor and is pinned by activityLabel.test.ts.
 *
 * This used to be a local copy of those rules that tested OCR+Analysis FIRST and had no
 * three-way branch at all, so selecting all three services produced "Video OCR and Analysis" —
 * naming two of the three the user had ordered and paid for, while the stored row said
 * VideoOcrAnalysisAndSegmentation. One resolver, one answer.
 */
function bundleTypeFor(services: ActivityTypes[]): ActivityTypes | undefined {
  if (services.length < 2) return undefined;
  return getAppActivity(services);
}

// The free segmentation line that rides along on a combined run: nothing billed, the whole
// list price shown as discount. Mirrors UserActivityLogCreator.IsFreeSegmentationMarker — it
// stands for a service rather than being the cycle's priced row, so it must not anchor a cycle.
const isFreeSegmentationMarker = (line: OrderBreakdownRow) =>
  line.activityTypeId === ActivityTypes.HighlightDetection &&
  line.credits === 0 &&
  line.grossPrice > 0 &&
  line.discount === line.grossPrice;

// Display name of an activity type, taken from the price list (the same ActivityType.Name the
// billed rows carry), e.g. "Video OCR and Segmentation".
const activityName = (type: ActivityTypes): string | undefined =>
  activityPricesStorage.prices.find((p) => p.id === type)?.service || undefined;

/**
 * A duration as hours / minutes / seconds.
 *
 * Hours are not optional here: a real inspection run takes them. Without an hours bucket every
 * hour simply piled onto the minute count, so a five-hour OCR pass rendered as "300m 18s" —
 * arithmetically correct (5 x 60 = 300) and unreadable as a length of time.
 *
 * Smaller units are kept once a larger one appears rather than being dropped for brevity: these
 * figures get compared against each other, and "5h" hiding anything from 5h00m to 5h59m would
 * make two visibly different runs look identical.
 */
function formatDuration(ms: number): string {
  if (!isFinite(ms) || ms < 0) return "—";
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

const t = (key: string) => translationService.translate(key);

export default observer(function OrderProcessSummary(props: Props) {
  const { ocrService, analysisService, segmentationService, orderStore, onRerun, compact, embedded } = props;

  // All three stores share the same file; ocrService is the one that loads `video`.
  const fileName: string = ocrService.video?.name ?? "";

  const anyRunning =
    ocrService.ocrRunning ||
    analysisService.analysisRunning ||
    segmentationService.segmentationRunning;

  // Live clock: tick once a second only while something is still running, so the elapsed time
  // counts up. Because startedAt comes from the backend, this elapsed value is correct even
  // right after a page refresh (unlike a client-only timer).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!anyRunning) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [anyRunning]);

  // Per-service facts. No longer a row each (see the single cycle row below) — they are what
  // the cycle's status, duration and re-run buttons are derived from.
  const rows = [
    {
      key: "ocr" as ServiceKey,
      label: t("ocrService.order.service.ocr"),
      activityType: ActivityTypes.VideoOcr,
      state: (ocrService.ocrSuccessful
        ? "success"
        : ocrService.ocrFailed
        ? "failed"
        : ocrService.ocrRunning
        ? "running"
        : "none") as ServiceState,
      startedAt: ocrService.startedAt as string | undefined,
      finishedAt: ocrService.finishedAt as string | undefined,
    },
    {
      key: "analysis" as ServiceKey,
      label: t("ocrService.order.service.analysis"),
      activityType: ActivityTypes.VideoAnalysis,
      state: (analysisService.analysisSuccessful
        ? "success"
        : analysisService.analysisFailed
        ? "failed"
        : analysisService.analysisRunning
        ? "running"
        : "none") as ServiceState,
      startedAt: analysisService.startedAt as string | undefined,
      finishedAt: analysisService.finishedAt as string | undefined,
    },
    {
      key: "segmentation" as ServiceKey,
      label: t("ocrService.order.service.segmentation"),
      activityType: ActivityTypes.HighlightDetection,
      state: (segmentationService.segmentationSuccessful
        ? "success"
        : segmentationService.segmentationFailed
        ? "failed"
        : segmentationService.segmentationRunning
        ? "running"
        : "none") as ServiceState,
      startedAt: segmentationService.startedAt as string | undefined,
      finishedAt: segmentationService.finishedAt as string | undefined,
    },
  ].filter((r) => r.state !== "none");

  // Nothing has been ordered/run for this file yet — render nothing.
  if (rows.length === 0) return null;

  /**
   * How long ONE service took, from its own backend timestamps: ocr_finished_at -
   * ocr_started_at for OCR, video_segmentation_finished_at - video_segmentation_started_at for
   * segmentation, video_auto_detection_* for analysis.
   *
   * This replaced a single wall-clock figure for the whole cycle (first start -> last finish).
   * The services run in parallel and take very different times, so that one number could not
   * say which of them the wait had actually been spent on — and on a combined run it read as
   * the time for a service that had in fact finished long before.
   *
   * parseServerDate, not new Date(): the timestamps arrive as UTC instants without a zone
   * suffix, so reading them as local time shifts the start by the viewer's offset — at UTC+2
   * every run read two hours longer than it was, and at a negative offset the duration goes
   * negative and renders as "—".
   */
  const serviceDuration = (row: (typeof rows)[number]) => {
    const start = parseServerDate(row.startedAt);
    if (start === undefined) return "—";
    // Still running: count up against the live clock. Correct even straight after a refresh,
    // because the start is the backend's own and not a client-side timer.
    if (row.state === "running") return formatDuration(now - start);
    const end = parseServerDate(row.finishedAt);
    return end === undefined ? "—" : formatDuration(end - start);
  };

  /**
   * One line per service that ran: its name and its own duration. Shared by both layouts —
   * caption-sized in the narrow sidebar (where it used to be a single caption line), body-sized
   * in the wide table so it matches the cells beside it. minWidth keeps the name and the time
   * from being flung to opposite edges of an otherwise narrow table cell.
   */
  const durationList = (
    <Stack spacing={0.25} sx={{ minWidth: 132 }}>
      {rows.map((row) => (
        <Stack
          key={row.key}
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography variant={compact ? "caption" : "body2"} color="text.secondary">
            {row.label}
          </Typography>
          <Typography
            variant={compact ? "caption" : "body2"}
            sx={{ whiteSpace: "nowrap" }}
          >
            {serviceDuration(row)}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );

  /**
   * The cycle's state, from its services. Running outranks failed: while anything is still in
   * flight the run is not over, and calling it "Failed" mid-way reads as the whole order
   * having collapsed. Failed then outranks success, so a partial failure stays visible rather
   * than being smoothed into "Completed".
   */
  const failedRows = rows.filter((r) => r.state === "failed");
  const cycleState: ServiceState = rows.some((r) => r.state === "running")
    ? "running"
    : failedRows.length
    ? "failed"
    : "success";

  const statusChip = (state: ServiceState) => {
    if (state === "success")
      return <Chip size="small" color="success" label={t("ocrService.order.status.success")} />;
    if (state === "failed")
      return <Chip size="small" color="error" label={t("ocrService.order.status.failed")} />;
    return <Chip size="small" color="info" label={t("ocrService.order.status.running")} />;
  };

  // The lines the backend actually billed. Shown explicitly so the real cost is visible even
  // when it was charged as one bundle rather than per service.
  const billedLines = orderStore.rows;

  // Which billed line is the cycle currently being charged: the newest priced (non-marker)
  // bundlable row. Rows come back unordered, so pick by timestamp — an upgrade never restamps
  // a row — falling back to document order on a tie.
  const cycleLineIndex = (() => {
    let best = -1;
    billedLines.forEach((line, i) => {
      if (!BUNDLABLE_TYPES.includes(line.activityTypeId)) return;
      if (isFreeSegmentationMarker(line)) return;
      if (best < 0) {
        best = i;
        return;
      }
      const bestAt = parseServerDate(billedLines[best].timestamp) ?? 0;
      const lineAt = parseServerDate(line.timestamp) ?? 0;
      if (lineAt >= bestAt) best = i;
    });
    return best;
  })();

  // The single type this run is billed as, from the services that actually ran — the same answer
  // the server's BundleTypeFor gives for the row it stores. Shared by the panel heading and by
  // the priced line's name while the run is still completing, so the two cannot disagree.
  const runCombinationType = bundleTypeFor(rows.map((r) => r.activityType));

  // The services that line already stands for (its bundle members, plus a free segmentation
  // marker written after it — same cycle).
  const cycleServices = (() => {
    if (cycleLineIndex < 0) return [] as ActivityTypes[];
    const line = billedLines[cycleLineIndex];
    const services = [...(BUNDLE_MEMBERS[line.activityTypeId] ?? [line.activityTypeId])];
    if (
      !services.includes(ActivityTypes.HighlightDetection) &&
      billedLines.some((l, i) => i > cycleLineIndex && isFreeSegmentationMarker(l))
    )
      services.push(ActivityTypes.HighlightDetection);
    return services;
  })();

  /**
   * A cycle's priced row is created by the FIRST service that succeeds and only UPGRADED to
   * the combination type as each later service calls in — so between those moments the line
   * is named after one service while the run is plainly a combination (OCR done, segmentation
   * still going, billed line reading "Video OCR" instead of "Video OCR and Segmentation").
   *
   * Name the line after the combination the run is heading for as soon as another service of
   * the same cycle is still in flight. The amount is left alone: it is what has actually been
   * charged so far and catches up by itself when the backend upgrades the row. A service that
   * is already part of the cycle is a re-run, which the backend bills as a NEW row — so the
   * existing line keeps its own name.
   */
  const pendingCombinationName = (() => {
    if (cycleLineIndex < 0) return undefined;
    const inFlight = rows
      .filter((r) => r.state === "running" && !cycleServices.includes(r.activityType))
      .map((r) => r.activityType);
    if (inFlight.length === 0) return undefined;
    // Name it after everything the RUN covers, not just what the priced row happens to name.
    // A build that billed per service left OCR on a row of its own, so [cycleServices, inFlight]
    // saw only Analysis + Segmentation and called a three-way run "Video Analysis and
    // Segmentation" — dropping a service the user ordered. The services are the authority here,
    // exactly as they are for cycleLabel, so both now read the same.
    return runCombinationType === undefined ? undefined : activityName(runCombinationType);
  })();

  // Name of one billed line: the combination it is heading for while a sibling service is
  // still running, otherwise exactly what the backend charged it as.
  const billedLabel = (line: OrderBreakdownRow, index: number, inline?: boolean) => {
    const pending = index === cycleLineIndex && pendingCombinationName !== undefined;
    const text = (
      <Typography
        component={inline ? "span" : "p"}
        variant="body2"
        sx={{ wordBreak: "break-word" }}
      >
        {pending ? pendingCombinationName : line.service}
      </Typography>
    );
    return pending ? (
      <Tooltip title={t("ocrService.order.pendingCombinationHint")}>{text}</Tooltip>
    ) : (
      text
    );
  };

  type Charge =
    | { kind: "own"; credits: number }
    | { kind: "bundled"; label: string }
    | { kind: "free" };

  /**
   * What the cycle cost, asked once for the whole run instead of once per service. A
   * combination is billed as a single row, which used to be reported identically on every
   * service line ("Included in Video OCR and Segmentation", once per service) — with one row
   * for the cycle it is simply the name of the package the run was charged as.
   */
  const cycleCharge = ((): Charge => {
    if (cycleLineIndex < 0) return { kind: "free" };
    const line = billedLines[cycleLineIndex];
    // A combination: name it. NOT its credits — the amount is on the billed line below, and
    // repeating it here would read as a second charge.
    if (BUNDLE_MEMBERS[line.activityTypeId]) return { kind: "bundled", label: line.service };
    // A single-service run has no package to name, so the amount is the useful thing to show.
    return line.credits > 0 ? { kind: "own", credits: line.credits } : { kind: "free" };
  })();

  // One button per failed service. Named when several failed so they can be told apart; a
  // lone failure keeps the plain "Re-run".
  const rerunButton = (row: (typeof rows)[number]) => (
    <Button key={row.key} size="small" variant="outlined" onClick={() => onRerun(row.key)}>
      {failedRows.length > 1
        ? `${t("ocrService.order.rerun")}: ${row.label}`
        : t("ocrService.order.rerun")}
    </Button>
  );

  const chargedCell = () => {
    // Still in flight: the cycle is not finally priced until the last service lands (the
    // backend upgrades the row as each one calls in), so there is no honest amount to show.
    if (cycleState === "running")
      return (
        <Typography variant="body2" color="text.secondary">
          {t("ocrService.order.pending")}
        </Typography>
      );

    const amount =
      cycleCharge.kind === "own" ? (
        <Coin amount={cycleCharge.credits} text=" " />
      ) : cycleCharge.kind === "bundled" ? (
        <Tooltip title={t("ocrService.order.bundledHint")}>
          <Chip
            size="small"
            variant="outlined"
            label={`${t("ocrService.order.includedIn")} ${cycleCharge.label}`}
            sx={{ maxWidth: "100%" }}
          />
        </Tooltip>
      ) : failedRows.length ? (
        // Nothing was billed and something failed — say so, that is the reassurance the user
        // is looking for. A PARTIAL failure keeps whatever was charged above instead: the
        // services that did succeed were paid for, and "Not charged" there would be false.
        <Tooltip title={t("ocrService.order.failedHint")}>
          <Typography variant="body2" color="success.main">
            {t("ocrService.order.notCharged")}
          </Typography>
        </Tooltip>
      ) : null;

    if (!failedRows.length) return amount;
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
        flexWrap="wrap"
        useFlexGap
      >
        {amount}
        {failedRows.map(rerunButton)}
      </Stack>
    );
  };

  /**
   * The one row this panel lists: the run itself. Its services are ordered, priced and
   * delivered as a single cycle, so a line each invited reading every one as its own order.
   *
   * Named after what actually RAN, not after the app: this row sits directly above a duration
   * list that says "Video Analysis", so labelling it "Inspection Insights" on an analysis-only
   * run read as a second, larger thing that had also happened. Derived from `rows` rather than
   * from the billed lines, so it is still right when the cycle has no priced row yet (or, on a
   * file finalized by an older build, none at all). The app name stays as the fallback for a
   * service mix that has no combination of its own.
   */
  const cycleLabel = (() => {
    if (runCombinationType !== undefined)
      return activityName(runCombinationType) ?? t("general.apps.ocr-service.title");
    return rows.length === 1 ? rows[0].label : t("general.apps.ocr-service.title");
  })();

  const title = (
    <Typography variant={compact ? "subtitle1" : "h6"} sx={{ mb: 1 }}>
      {!embedded && t("ocrService.order.title")}
      {/* Which file this order belongs to. The page can be opened straight from the history
          list, so the name is not otherwise on screen at this point. */}
      {fileName && (
        <Typography
          component={compact ? "div" : "span"}
          variant="body2"
          color="text.secondary"
          sx={{ ml: compact ? 0 : 1, wordBreak: "break-all" }}
        >
          {compact ? fileName : `(${t("ocrService.order.fileName")}: ${fileName})`}
        </Typography>
      )}
    </Typography>
  );

  const billedBlock = billedLines.length > 0 && (
    <>
      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" color="text.secondary">
        {t("ocrService.order.billedLines")}
      </Typography>
      {billedLines.map((line, i) => (
        <Stack
          key={`${line.activityTypeId}-${i}`}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          {billedLabel(line, i)}
          <Coin amount={line.credits} text=" " />
        </Stack>
      ))}
    </>
  );

  const totalRow = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      sx={{ mt: 1 }}
    >
      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
        {t("ocrService.order.total")}
      </Typography>
      <Coin amount={orderStore.totalCredits} text=" " />
    </Stack>
  );

  // Narrow sidebar: a 4-column table does not fit, so the cycle becomes a small stacked block
  // (name + status on one line, then the charge and a duration per service underneath).
  if (compact) {
    return (
      <Paper
        variant="outlined"
        sx={embedded ? { p: 0, width: "100%", border: 0, bgcolor: "transparent" } : { p: 1.5, width: "100%" }}
      >
        {title}
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="body2">{cycleLabel}</Typography>
            {statusChip(cycleState)}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ mt: 0.5 }}
          >
            {/* The charge kept the right-hand side it had when the duration was a single line,
                which left it sitting on a row labelled "Duration:" once the duration became a
                list — so a pending charge of "—" read as an unknown duration. Each value now
                carries its own label. */}
            <Typography variant="caption" color="text.secondary">
              {t("ocrService.order.chargedColumn")}
            </Typography>
            <Box sx={{ textAlign: "right" }}>{chargedCell()}</Box>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {t("ocrService.order.durationLabel")}
          </Typography>
          <Box sx={{ pl: 1, mt: 0.25 }}>{durationList}</Box>
        </Box>
        {billedBlock}
        {totalRow}
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, width: "100%" }}>
      {title}
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("ocrService.order.serviceColumn")}</TableCell>
              <TableCell>{t("ocrService.order.statusColumn")}</TableCell>
              <TableCell>{t("ocrService.order.timeColumn")}</TableCell>
              <TableCell align="right">{t("ocrService.order.chargedColumn")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{cycleLabel}</TableCell>
              <TableCell>{statusChip(cycleState)}</TableCell>
              <TableCell>{durationList}</TableCell>
              <TableCell align="right">{chargedCell()}</TableCell>
            </TableRow>
            {billedLines.map((line, i) => (
              <TableRow key={`billed-${line.activityTypeId}-${i}`}>
                <TableCell colSpan={3} sx={{ color: "text.secondary" }}>
                  {t("ocrService.order.billedLines")}: {billedLabel(line, i, true)}
                </TableCell>
                <TableCell align="right">
                  <Coin amount={line.credits} text=" " />
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={3} sx={{ fontWeight: "bold", border: 0 }}>
                {t("ocrService.order.total")}
              </TableCell>
              <TableCell align="right" sx={{ border: 0 }}>
                <Coin amount={orderStore.totalCredits} text=" " />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
});
